import { LitElement, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { styles } from './asset-detail-page.style.js';
import { PhotoUploadService } from '../../services/photo-upload.service.js';
import { 
  getLocationsByCompany, 
  getLocations, 
  updateAsset, 
  getPartsByAsset,
  getAssetById
} from '@cmms/api';
import { Firestore } from 'firebase/firestore';
import '../asset-viewer/parts/index.js';

@customElement('asset-detail-page')
export class AssetDetailPage extends LitElement {
  static styles = styles;

  @property({ type: String }) assetId: string = '';
  @property({ type: String }) companyId: string = '';
  @property({ type: Object }) firestore: Firestore = null as any;
  @property({ type: String }) userRole: string = '';

  @state() private asset: any = null;
  @state() private activeTab = 'info';
  @state() private partsLoading = false;
  @state() private partsError: string | null = null;
  @state() private parts: Array<any> = [];
  @state() private isUploadingPhoto = false;
  @state() private photoUploadError = '';
  @state() private locations: Array<{ id: string; altId?: string; name: string }> = [];
  @state() private hasChanges = false;
  @state() private isLoading = true;
  @state() private loadError: string | null = null;

  private photoUploadService = new PhotoUploadService();

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('form-changed', this.handleFormChanged);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('form-changed', this.handleFormChanged);
  }

  private handleFormChanged = (event: Event) => {
    const customEvent = event as CustomEvent;
    this.hasChanges = customEvent.detail.hasChanges;
  }

  updated(changed: Map<string, unknown>) {
    if (changed.has('activeTab')) {
      if (this.activeTab === 'parts') this.loadParts();
    }
    
    // Load asset when assetId or companyId becomes available
    if ((changed.has('assetId') || changed.has('companyId')) && this.assetId && this.companyId && this.firestore) {
      this.loadAsset();
      this.loadLocations();
    }
  }

  private async loadAsset() {
    if (!this.assetId || !this.firestore || !this.companyId) {
      this.isLoading = false;
      this.loadError = 'Missing required parameters';
      return;
    }
    
    try {
      this.isLoading = true;
      this.loadError = null;
      
      const result = await getAssetById(this.firestore, this.assetId, this.companyId);
      
      if (result.error) {
        this.loadError = result.error;
        return;
      }
      
      this.asset = result.data;
    } catch (error: any) {
      this.loadError = error?.message || 'Failed to load asset';
    } finally {
      this.isLoading = false;
    }
  }

  private async loadLocations() {
    try {
      let result: any;
      if (this.companyId) {
        result = await getLocationsByCompany(this.firestore, this.companyId);
      }
      if (!result || !result.data || result.data.length === 0) {
        result = await getLocations(this.firestore);
      }
      const raw = (result?.data ?? []) as any[];
      
      this.locations = raw.map((l: any) => ({
        id: l.id || l.$id || l.location_id,
        altId: l.location_id || l.model?.location_id,
        name: l.name || l.model?.name || 'Unknown'
      }));
    } catch (error) {
      // Silent fail - locations will remain empty
    }
  }

  private handlePhotoClick() {
    (this.shadowRoot?.querySelector('.photo-file-input') as HTMLElement)?.click();
  }

  private openPart(part: any) {
    const viewer = document.createElement('part-viewer') as any;
    viewer.open = true;
    viewer.part = part;
    document.body.appendChild(viewer);
    viewer.addEventListener('modal-closed', () => document.body.removeChild(viewer), { once: true });
  }

  private async loadParts() {
    const ids = Array.from(new Set([
      this.asset?.id,
      this.asset?.asset_id,
      this.asset?.model?.asset_id
    ].filter(Boolean).map((v: any) => String(v))));
    
    if (ids.length === 0) { 
      this.parts = []; 
      return; 
    }
    
    try {
      this.partsLoading = true; 
      this.partsError = null;
      const collected: any[] = [];
      
      for (const id of ids) {
        const resp: any = await getPartsByAsset(this.firestore, id, this.companyId);
        const raw = Array.isArray(resp) ? resp : (resp?.data || []);
        if (raw && raw.length) collected.push(...raw);
      }
      
      // Normalize shape
      this.parts = collected.map((part: any) => ({
        id: part.id || part.$id,
        name: part.name || part.model?.name || 'Unnamed',
        stock_qty: part.stock_qty ?? part.model?.stock_qty ?? 0,
        min_qty: part.min_qty ?? part.model?.min_qty ?? 0,
        asset_id: part.asset_id || part.model?.asset_id || ''
      }));
    } catch (e: any) {
      this.partsError = e?.message || 'Failed to load parts';
      this.parts = [];
    } finally {
      this.partsLoading = false;
    }
  }

  private async handleTabChange(event: any) {
    this.activeTab = event.detail.name;
    if (event.detail.name === 'parts') {
      await this.loadParts();
    }
  }

  private async handlePhotoFileSelect(e: Event) {
    const input = e.target as HTMLInputElement;
    if (input.files && input.files[0]) await this.uploadPhoto(input.files[0]);
  }

  private async uploadPhoto(file: File) {
    const firestoreId = this.asset?.id ?? this.asset?.asset_id;
    const assetId = this.asset?.asset_id ?? this.asset?.id;
    
    if (!firestoreId || !assetId) { 
      this.photoUploadError = 'Asset ID not found'; 
      return; 
    }

    try {
      this.isUploadingPhoto = true;
      this.photoUploadError = '';

      // If asset already has a photo, delete the old one first
      if (this.asset?.photo_url) {
        try {
          const deleteResult = await this.photoUploadService.deleteAssetPhoto(assetId, 'default', this.asset.photo_url);
          if (!deleteResult.success) {
            console.warn('Failed to delete old photo:', deleteResult.error);
          }
        } catch (error) {
          console.warn('Error deleting old photo:', error);
        }
      }

      const result = await this.photoUploadService.uploadPhoto(file, assetId, 'default');
      
      if (result.success && result.url) {
        try {
          const updateResult = await updateAsset(this.firestore, firestoreId, { 
            photo_url: result.url, 
            updated_at: Date.now() 
          });
          
          if ((updateResult as any)?.error) { 
            this.photoUploadError = 'Photo uploaded but failed to update database'; 
            return; 
          }
        } catch {
          this.photoUploadError = 'Photo uploaded but failed to update database';
          return;
        }

        // Update the local asset object to trigger re-render
        this.asset = { ...this.asset, photo_url: result.url };
      } else {
        this.photoUploadError = (result as any).error || 'Failed to upload photo';
      }
    } catch (error: any) {
      this.photoUploadError = error?.message || 'Failed to upload photo';
    } finally {
      this.isUploadingPhoto = false;
      const input = this.shadowRoot?.querySelector('.photo-file-input') as HTMLInputElement;
      if (input) input.value = '';
    }
  }

  private handleEdit() {
    const assetInfoComponent = this.shadowRoot?.querySelector('asset-info') as any;
    if (assetInfoComponent) {
      assetInfoComponent.isEditing = true;
    }
  }

  private handleCancel() {
    const assetInfoComponent = this.shadowRoot?.querySelector('asset-info') as any;
    if (assetInfoComponent) {
      assetInfoComponent.handleCancel();
    }
    this.hasChanges = false;
  }

  private async handleSave() {
    const assetInfoComponent = this.shadowRoot?.querySelector('asset-info') as any;
    if (assetInfoComponent) {
      await assetInfoComponent.handleSave();
      this.hasChanges = false;
    }
  }

  private downloadQRCode() {
    if (this.asset?.qr_code_url) {
      const link = document.createElement('a');
      link.href = this.asset.qr_code_url;
      link.download = `asset-${this.asset.id || this.asset.name}-qr-code.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }

  private handleBackClick() {
    this.dispatchEvent(new CustomEvent('navigate-back', {
      bubbles: true,
      composed: true
    }));
  }

  render() {
    if (this.isLoading) {
      return html`
        <div class="page-container">
          <div class="loading-container">
            <sl-spinner size="large"></sl-spinner>
          </div>
        </div>
      `;
    }

    if (this.loadError || !this.asset) {
      return html`
        <div class="page-container">
          <div class="error-container">
            <sl-alert variant="danger" open>
              <sl-icon slot="icon" name="exclamation-triangle"></sl-icon>
              ${this.loadError || 'Asset not found'}
            </sl-alert>
            <sl-button @click=${this.handleBackClick} style="margin-top: 1rem;">
              <sl-icon name="arrow-left" slot="prefix"></sl-icon>
              Back to Assets
            </sl-button>
          </div>
        </div>
      `;
    }

    // Check if user is shop floor
    if (this.isShopFloor()) {
      return this.renderShopFloorView();
    }

    return this.renderAdminView();
  }

  private isShopFloor(): boolean {
    const userRole = (this.userRole || '').toLowerCase();
    return userRole === 'shop floor' || userRole === 'shopfloor';
  }

  private renderShopFloorView() {
    const assetName = this.asset.name || '';
    const assetParent = this.asset.parent_name || 'Root Asset';

    return html`
      <div class="page-container">
        <div class="page-header">
          <sl-button class="back-button" variant="text" @click=${this.handleBackClick}>
            <sl-icon name="arrow-left" slot="prefix"></sl-icon>
            Back
          </sl-button>
          <h1 style="margin: 0; font-size: 1.5rem; color: var(--sl-color-neutral-900);">${assetName}</h1>
        </div>

        <div class="asset-info">
          <div class="asset-image-placeholder">
            ${this.asset.photo_url ? html`
              <img src="${this.asset.photo_url}" alt="Asset photo" />
            ` : html`
              <sl-icon name="gears" style="font-size: 3rem; color: var(--sl-color-neutral-400);"></sl-icon>
            `}
          </div>

          <div class="asset-details">
            <div class="asset-name">${assetName}</div>
            <div class="asset-location">${assetParent}</div>
            <div class="asset-location">ID: ${this.assetId}</div>
          </div>
        </div>

        <div class="content-area" style="padding: 1.5rem;">
          <div style="background: white; border-radius: 12px; padding: 2rem; text-align: center; box-shadow: 0 2px 4px rgba(0,0,0,0.05);">
            <sl-icon name="file-text" style="font-size: 4rem; color: var(--sl-color-primary-600); margin-bottom: 1rem;"></sl-icon>
            <h2 style="margin: 0 0 0.5rem 0; color: var(--sl-color-neutral-900);">Need Maintenance?</h2>
            <p style="margin: 0 0 2rem 0; color: var(--sl-color-neutral-600);">
              File a work order for this asset if you notice any issues or need maintenance.
            </p>
            <sl-button 
              variant="primary" 
              size="large" 
              @click=${this.handleFileWorkOrder}
              style="min-width: 200px;">
              <sl-icon name="file-plus" slot="prefix"></sl-icon>
              File Work Order
            </sl-button>
          </div>
        </div>
      </div>
    `;
  }

  private handleFileWorkOrder() {
    // Create and open work order modal with asset pre-selected
    const modal = document.createElement('work-order-modal') as any;
    modal.open = true;
    modal.companyId = this.companyId;
    modal.firestore = this.firestore;
    modal.userRole = this.userRole;
    modal.formData = {
      description: '',
      asset_id: this.assetId,
      priority_cat: 2, // Medium
      status_cat: 1, // Open
      assigned_to: ''
    };
    
    document.body.appendChild(modal);
    
    modal.addEventListener('modal-closed', () => {
      document.body.removeChild(modal);
    }, { once: true });
  }

  private renderAdminView() {
    const assetName = this.asset.name || '';
    const assetParent = this.asset.parent_name || 'Root Asset';

    return html`
      <div class="page-container">
        <div class="page-header">
          <sl-button class="back-button" variant="text" @click=${this.handleBackClick}>
            <sl-icon name="arrow-left" slot="prefix"></sl-icon>
            Back
          </sl-button>
          <h1 style="margin: 0; font-size: 1.5rem; color: var(--sl-color-neutral-900);">Asset Details</h1>
        </div>

        <div class="asset-info">
          <div class="asset-image-placeholder ${this.isUploadingPhoto ? 'uploading' : ''}" @click=${this.handlePhotoClick}>
            ${this.asset.photo_url ? html`
              <img src="${this.asset.photo_url}" alt="Asset photo" />
              <div class="upload-overlay">
                <sl-icon name="camera"></sl-icon>
              </div>
            ` : html`
              <sl-icon name="camera" style="font-size: 3rem; color: var(--sl-color-neutral-400);"></sl-icon>
            `}
          </div>

          <div class="asset-details">
            <div class="asset-name">${assetName}</div>
            <div class="asset-location">${assetParent}</div>
            <div class="asset-location">ID: ${this.assetId}</div>
            ${this.photoUploadError ? html`
              <sl-alert variant="warning" open style="margin-top: 0.5rem;">
                ${this.photoUploadError}
              </sl-alert>
            ` : ''}
          </div>

          ${this.asset?.qr_code_url ? html`
            <div class="header-qr-code">
              <img 
                src="${this.asset.qr_code_url}" 
                alt="QR Code for ${this.asset.name}"
                style="width: 120px; height: 120px; border: 2px solid var(--sl-color-neutral-300); border-radius: 12px;"
              />
              <sl-button 
                size="small"
                @click=${this.downloadQRCode}
                title="Download QR Code"
              >
                <sl-icon name="download" slot="prefix"></sl-icon>
                Download QR
              </sl-button>
            </div>
          ` : ''}
        </div>

        <div class="content-area">
          <sl-tab-group @sl-tab-show=${this.handleTabChange}>
            <sl-tab slot="nav" panel="info">
              <sl-icon name="info-circle"></sl-icon> Info
            </sl-tab>
            <sl-tab slot="nav" panel="pms">
              <sl-icon name="wrench"></sl-icon> PMs
            </sl-tab>
            <sl-tab slot="nav" panel="wos">
              <sl-icon name="file-text"></sl-icon> WOs
            </sl-tab>
            <sl-tab slot="nav" panel="parts">
              <sl-icon name="gear"></sl-icon> Parts
            </sl-tab>
            <sl-tab slot="nav" panel="downtimes">
              <sl-icon name="clock"></sl-icon> Downtimes
            </sl-tab>
            <sl-tab slot="nav" panel="reports" disabled>
              <sl-icon name="bar-chart"></sl-icon> Reports
            </sl-tab>

            <sl-tab-panel name="info">
              <asset-info 
                .asset=${this.asset}
                .companyId=${this.companyId}
                .locations=${this.locations}>
              </asset-info>
            </sl-tab-panel>

            <sl-tab-panel name="wos">
              <asset-wos 
                .asset=${this.asset}
                .companyId=${this.companyId}>
              </asset-wos>
            </sl-tab-panel>

            <sl-tab-panel name="pms">
              <asset-pms 
                .asset=${this.asset}
                .companyId=${this.companyId}>
              </asset-pms>
            </sl-tab-panel>

            <sl-tab-panel name="parts">
              <div style="padding: 1.5rem;">
                <div class="info-description">Parts linked to this asset</div>
                ${this.partsLoading ? html`
                  <div style="display:flex;justify-content:center;padding:1rem">
                    <sl-spinner></sl-spinner>
                  </div>
                ` : this.partsError ? html`
                  <sl-alert variant="danger" open>${this.partsError}</sl-alert>
                ` : this.parts.length === 0 ? html`
                  <div class="field-row" style="justify-content:center;color:var(--sl-color-neutral-600)">
                    No parts linked to this asset
                  </div>
                ` : html`
                  <div class="data-table-container">
                    <table class="data-table">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Stock</th>
                          <th>Min</th>
                        </tr>
                      </thead>
                      <tbody>
                        ${this.parts.map((part: any) => html`
                          <tr @click=${() => this.openPart(part)}>
                            <td>${part.name}</td>
                            <td>${part.stock_qty}</td>
                            <td>${part.min_qty}</td>
                          </tr>
                        `)}
                      </tbody>
                    </table>
                  </div>
                `}
              </div>
            </sl-tab-panel>

            <sl-tab-panel name="downtimes">
              <asset-downtimes 
                .asset=${this.asset} 
                .companyId=${this.companyId}>
              </asset-downtimes>
            </sl-tab-panel>

            <sl-tab-panel name="reports">
              <div style="padding: 1.5rem;">
                <div class="info-description">Asset Reports - Coming Soon</div>
                <div class="field-row" style="justify-content:center;color:var(--sl-color-neutral-600)">
                  Reports functionality will be available soon
                </div>
              </div>
            </sl-tab-panel>
          </sl-tab-group>

          <div class="action-bar">
            ${(() => {
              const assetInfoComponent = this.shadowRoot?.querySelector('asset-info') as any;
              const isEditing = assetInfoComponent?.isEditing || false;
              
              return isEditing ? html`
                <sl-button
                  variant="neutral"
                  size="large"
                  @click=${this.handleCancel}>
                  Cancel
                </sl-button>
                <sl-button
                  variant="primary"
                  size="large"
                  @click=${this.handleSave}
                  ?loading=${assetInfoComponent?.isSaving || false}>
                  Save Changes
                </sl-button>
              ` : html`
                <sl-button
                  variant="primary"
                  size="large"
                  @click=${this.handleEdit}>
                  <sl-icon name="pencil" slot="prefix"></sl-icon>
                  Edit Asset
                </sl-button>
              `;
            })()}
          </div>
        </div>

        <!-- Hidden file input for photo upload -->
        <input 
          type="file" 
          class="photo-file-input" 
          accept="image/*" 
          @change=${this.handlePhotoFileSelect} 
          style="display:none;" 
        />
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'asset-detail-page': AssetDetailPage;
  }
}

